import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import ProductCardForm from "../ProductCardForm";
import ProductCardPrice from "../ProductCardPrice";
import { formatMoney, formatMoneyKg, formatMoneyL } from "@/utils/formatMoney";

const ProductSlider = ({ slides, limit, sectionId, simpleAtc, showLearnMoreButton, showReviewStars }) => {
  return (
    <div
      className="product-slider grid grid-cols-2 gap-4 sm:gap-8 lg:gap-10 pb-0 justify-center items-center min-[600px]:grid-cols-3 min-[600px]:grid-rows-[1fr_0] min-[900px]:grid-cols-4 lg:grid-cols-4 overflow-hidden"
    >        
      {slides.filter((_, i) => i < limit).map((props, i) => (
        <ProductSlide {...props} sectionId={sectionId} simpleAtc={simpleAtc} key={`${i}-${sectionId}`} showLearnMoreButton={showLearnMoreButton} showReviewStars={showReviewStars} />
      ))}
    </div>
  );
};

ProductSlider.propTypes = {
  slides: PropTypes.array.isRequired,
  sectionId: PropTypes.string,
  limit: PropTypes.number,
  simpleAtc: PropTypes.bool,
  showReviewStars: PropTypes.bool,
  showLearnMoreButton: PropTypes.bool,
};

const ProductSlide = ({
  defaultVariantId,
  defaultSellingPlanId,
  sectionId,
  product,
  dataSku,
  simpleAtc,
  showLearnMoreButton,
  showReviewStars,
}) => {
  const { t } = useTranslation();

  const tag = product?.tags?.find(tag => tag.indexOf("badge:"));

  useEffect(() => {
    const trySetRatingStar = () => {
      if (typeof ratingSnippet === "function") {
        window.ratingSnippet("ruk_rating_snippet", {
          store: window.Theme.reviewsio_store_id,
          mode: "default",
          color: "#0B2E24",
          linebreak: false,
          usePolaris: true,
          showEmptyStars: false,
          text: "Reviews",
          singularText: "Reviews",
        });
      } else {
        setTimeout(() => {
          trySetRatingStar();
        }, 500);
      }
    };
    trySetRatingStar();
  }, []);

  return (
    <div className="flex flex-col h-full group swiper-slide max-w-[300px] lg:max-w-none">
      <a
        href={product.url}
        aria-label={product.title}
        className="relative block aspect-square w-full overflow-hidden rounded-3xl mb-4"
      >
        {product.featured_image && (
          <img
            sizes="(min-width: 768px) calc(50vw - 2rem), (min-width: 1024px) calc(25vw - 2rem), calc(100vw - 2rem)"
            srcSet={product.featured_image.srcset}
            src={product.featured_image.src}
            alt={product.featured_image.alt}
            loading="lazy"
            className="image object-cover transition-opacity hover:opacity-70"
          />
        )}

        {tag && (
          <span
            key={tag}
            className="absolute left-3 top-4 rounded-full bg-deep-green px-[0.75rem] py-[0.38rem] font-dm-sans text-xs font-medium capitalize not-italic leading-[1] text-white"
          >
            {tag?.replace("_", " ").replace("badge:", "")}
          </span>
        )}
      </a>

      <div className={`flex w-full items-start justify-center text-center xl:text-left sm:justify-between ${!simpleAtc && 'sm:text-left'}  ${simpleAtc && 'min-[640px]:flex-col'}  xl:flex-row gap-x-1`}>
        <a href={product.url} className="h6 text-[1.06rem] leading-[130%] text-deep-green w-full text-left">
          {product.title}
        </a>
        {
          !product.available ?
            <div>{t("common.sold_out")}</div>
          : simpleAtc ? (
            <div className="product-card-price flex flex-col flex-grow items-center gap-1 mt-[2px] text-left w-full xl:w-fit justify-center xl:justify-start ">
              <span className="font-dm-sans text-lg font-normal not-italic leading-[140%] tracking-[0.00875rem] text-deep-green md:tracking-normal">
                {formatMoney(product.price)}
              </span>
              {(product.kg_price || product.l_price) && (
                <div className="flex items-start gap-1">
                  <span className="italic opacity-50 text-xs">
                    {product.kg_price ? formatMoneyKg(product.kg_price) : formatMoneyL(product.l_price)}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <ProductCardPrice
              sectionId={sectionId}
              prices={product.prices}
              defaultVariantId={defaultVariantId}
              defaultSellingPlanId={defaultSellingPlanId}
            />
          )
        }
      </div>

      {showReviewStars &&
        <div aria-label={product.title} className="ruk_rating_snippet stars" data-sku={dataSku}></div>
      }

      <div className="pt-2 mt-auto">
        <ProductCardForm product={product} sectionId={sectionId} simpleAtc={simpleAtc} showLearnMoreButton={showLearnMoreButton} />
      </div>
    </div>
  );
};

ProductSlide.propTypes = {
  sectionId: PropTypes.string,
  dataSku: PropTypes.string,
  defaultVariantId: PropTypes.number,
  defaultSellingPlanId: PropTypes.number,
  product: PropTypes.object,
};

export default ProductSlider;
